/**
 * Access and roles (access.people.list → /loans/access/people). LOP-owned.
 *
 * Bill 2026-11-03. Who holds a loans persona or a loans admin grant, and what each persona allows. An admin sets a person's personas
 * (the full set is sent; the API works out what to add and remove, dates each change and records who and why). The API's own rules
 * decide, and its refusals are shown in the modal:
 *   - Chief Lending Officer, CLO approver and Executive Officer come from the person's officer designation, not from this list;
 *   - admin personas come from the loans admin grant, not from this list;
 *   - an admin may only change personas their grant covers; the last platform administrator cannot be removed.
 *
 * Layout follows the house editor pattern (src/modules/System/SettingsPage.tsx): a Card with counts in the title and the filter /
 * "+ Add person" in its actions slot, a per-row Edit button, every edit in a ModalShell carrying the change reason, the saving state
 * and the error; a dismissible toast on success.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { BoolField, TextField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';
import { ReasonField, asciiReason, reasonProblem } from '@/components/ChangeReason';
import { loansApi } from '../loansApi';
import { errorText, fmtCt } from '../writeups/common';
import { modalError } from '../adminKit';

interface Assignment {
  code: string;
  effectiveFromUtc: string;
  effectiveToUtc: string | null;
  grantedBy: string;
  grantedAtUtc: string;
}

interface AdminGrant {
  account: string;
  area: string;
  effectiveFromUtc: string;
  effectiveToUtc: string | null;
  grantedBy: string;
}

interface Person {
  officerId: number | null;
  operatorNumber: number | null;
  displayName: string;
  email: string;
  adminAccount: string | null;
  jobRole: string | null;
  onRoster: boolean;
  assignments: Assignment[];
  designations: string[];
  adminGrants: AdminGrant[];
}

interface Persona {
  code: string;
  name: string;
  description: string | null;
  module: string;
  active: boolean;
  assignable: boolean;
  notAssignableReason: string | null;
  coveredByYourGrant: boolean;
  capabilities: { code: string; words: string }[];
}

interface AccessAnswer {
  people: Person[];
  personas: Persona[];
}

interface PutAnswer {
  message: string;
  added: string[];
  removed: string[];
  effectiveFromUtc: string | null;
}

interface Draft {
  person: Person;
  chosen: Set<string>;
}

const JOB_ROLES: Record<string, string> = {
  lo: 'Loan officer',
  la: 'Loan assistant',
  cds: 'Closing documents',
  pcs: 'Post-closing',
  st: 'Servicing',
  rm: 'Risk management',
  other: 'Other staff',
};
const jobRoleLabel = (r: string | null): string => (r ? JOB_ROLES[r.toLowerCase()] ?? r : '—');

const GRANT_AREAS: Record<string, string> = { global: 'Global admin', functional: 'Functional admin', risk: 'Risk admin' };
const grantLabel = (a: string): string => GRANT_AREAS[a] ?? a;

const DESIGNATION_NAMES: Record<string, string> = { CLO: 'Chief Lending Officer', CLOA: 'CLO approver', EO: 'Executive Officer' };

const holdsAnything = (p: Person): boolean => !p.onRoster || p.assignments.length > 0 || p.designations.length > 0 || p.adminGrants.length > 0;

export function AccessRolesPage(): JSX.Element {
  const [data, setData] = useState<AccessAnswer | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [filter, setFilter] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const [picking, setPicking] = useState(false);
  const [pickFilter, setPickFilter] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadError(null);
      try {
        const r = await loansApi.get<AccessAnswer>('v3/loans/admin/access');
        if (!cancelled) setData({ people: r.people ?? [], personas: r.personas ?? [] });
      } catch (e) {
        if (!cancelled) setLoadError(errorText(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const personaByCode = useMemo(() => new Map((data?.personas ?? []).map((p) => [p.code.toUpperCase(), p])), [data]);
  const personaName = useCallback(
    (code: string): string => personaByCode.get(code.toUpperCase())?.name ?? DESIGNATION_NAMES[code.toUpperCase()] ?? code,
    [personaByCode],
  );

  const holders = useMemo(() => (data?.people ?? []).filter(holdsAnything), [data]);
  const filtered = useMemo(() => {
    const n = filter.trim().toLowerCase();
    if (!n) return holders;
    return holders.filter((p) =>
      [p.displayName, p.email, jobRoleLabel(p.jobRole), ...p.assignments.map((a) => personaName(a.code)), ...p.designations.map(personaName),
        ...p.adminGrants.map((g) => grantLabel(g.area))]
        .join(' ')
        .toLowerCase()
        .includes(n),
    );
  }, [holders, filter, personaName]);

  const candidates = useMemo(() => {
    const n = pickFilter.trim().toLowerCase();
    return (data?.people ?? [])
      .filter((p) => p.onRoster && p.officerId !== null && p.assignments.length === 0)
      .filter((p) => !n || `${p.displayName} ${p.email}`.toLowerCase().includes(n))
      .slice(0, 50);
  }, [data, pickFilter]);

  const resetModal = (): void => {
    setReason('');
    setTried(false);
    setSaveError(null);
  };

  const openEdit = (p: Person): void => {
    resetModal();
    setPicking(false);
    setDraft({ person: p, chosen: new Set(p.assignments.map((a) => a.code.toUpperCase())) });
  };

  const closeModal = (): void => {
    if (saving) return;
    setDraft(null);
    setPicking(false);
    setSaveError(null);
  };

  /** The codes sent: the assignable boxes ticked. Admin personas and designations are never sent (the API keeps them). */
  const desired = (d: Draft): string[] =>
    [...d.chosen].filter((c) => {
      const p = personaByCode.get(c);
      return p !== undefined && p.assignable;
    });

  const problem = ((): string | null => {
    if (!draft) return null;
    const before = new Set(draft.person.assignments.map((a) => a.code.toUpperCase()).filter((c) => personaByCode.get(c)?.assignable));
    const after = new Set(desired(draft));
    if (before.size === after.size && [...before].every((c) => after.has(c))) return 'Nothing has changed.';
    return null;
  })();

  const save = async (): Promise<void> => {
    if (!draft || draft.person.officerId === null) return;
    setTried(true);
    if (problem) {
      setSaveError(problem);
      return;
    }
    if (reasonProblem(reason, { asciiOnly: true })) return;
    setSaving(true);
    setSaveError(null);
    try {
      const r = await loansApi.send<PutAnswer>(
        'PUT',
        `v3/loans/admin/access/people/${draft.person.officerId}/personas`,
        { personas: desired(draft) },
        asciiReason(reason),
      );
      const parts = [
        r.added?.length ? `gave ${r.added.join(', ')}` : '',
        r.removed?.length ? `removed ${r.removed.join(', ')}` : '',
      ].filter(Boolean);
      setToast(
        parts.length
          ? `${draft.person.displayName}: ${parts.join('; ')}. In effect from ${fmtCt(r.effectiveFromUtc)}.`
          : `${draft.person.displayName}: ${r.message}`,
      );
      setDraft(null);
      refresh();
    } catch (e) {
      setSaveError(modalError(e));
    } finally {
      setSaving(false);
    }
  };

  const columns: ReadonlyArray<DataTableColumn<Person>> = [
    { header: 'Name', cell: (p) => <span className="cell-strong">{p.displayName}</span>, sortKey: (p) => p.displayName },
    {
      header: 'Email',
      cell: (p) => (
        <span className="cell-desc">
          {p.email}
          {p.adminAccount && <div className="cell-dim">admin account {p.adminAccount}</div>}
        </span>
      ),
      sortKey: (p) => p.email,
    },
    { header: 'Job role', cell: (p) => jobRoleLabel(p.jobRole), sortKey: (p) => jobRoleLabel(p.jobRole) },
    {
      header: 'Personas',
      cell: (p) =>
        p.assignments.length || p.designations.length ? (
          <span>
            {p.assignments.map((a) => (
              <span key={a.code}>
                <Pill tone={personaByCode.get(a.code.toUpperCase())?.active === false ? 'muted' : 'info'}>{personaName(a.code)}</Pill>{' '}
              </span>
            ))}
            {p.designations.map((c) => (
              <span key={c}>
                <Pill tone="ok">{personaName(c)}</Pill>{' '}
              </span>
            ))}
          </span>
        ) : (
          <span className="cell-dim">—</span>
        ),
      sortKey: (p) => p.assignments.length + p.designations.length,
    },
    {
      header: 'Admin grant',
      cell: (p) =>
        p.adminGrants.length ? (
          <span>
            {p.adminGrants.map((g) => (
              <span key={`${g.account}-${g.area}`}>
                <Pill tone="warn">{grantLabel(g.area)}</Pill>{' '}
              </span>
            ))}
          </span>
        ) : (
          <span className="cell-dim">—</span>
        ),
      sortKey: (p) => p.adminGrants.length,
    },
    {
      header: '',
      cell: (p) => (p.onRoster ? <Button onClick={() => openEdit(p)}>Edit</Button> : <span className="cell-dim">not on the roster</span>),
    },
  ];

  const personaColumns: ReadonlyArray<DataTableColumn<Persona>> = [
    {
      header: 'Persona',
      cell: (p) => (
        <>
          <span className="cell-strong">{p.name}</span>
          {!p.active && (
            <>
              {' '}
              <Pill tone="muted">switched off</Pill>
            </>
          )}
        </>
      ),
      sortKey: (p) => p.name,
    },
    { header: 'Description', cell: (p) => (p.description ? <span className="cell-desc">{p.description}</span> : <span className="cell-dim">—</span>) },
    {
      header: 'Given out',
      cell: (p) => (p.assignable ? <Pill tone="ok">on this page</Pill> : <span className="cell-desc">{p.notAssignableReason}</span>),
    },
    {
      header: 'What it allows',
      cell: (p) =>
        p.capabilities.length ? (
          <ul className="link-list">
            {p.capabilities.map((c) => (
              <li key={c.code} className="cell-desc">
                {c.words}
              </li>
            ))}
          </ul>
        ) : (
          <span className="cell-dim">nothing beyond opening the app</span>
        ),
    },
  ];

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load access and roles: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!data) return <Spinner label="Loading access and roles…" />;

  const assignable = data.personas.filter((p) => p.assignable);
  const heldElsewhere = draft
    ? [
        ...draft.person.assignments
          .filter((a) => !personaByCode.get(a.code.toUpperCase())?.assignable)
          .map((a) => ({ code: a.code, why: personaByCode.get(a.code.toUpperCase())?.notAssignableReason ?? '' })),
        ...draft.person.designations.map((c) => ({ code: c, why: 'Comes from the person’s officer designation, not from this list' })),
      ]
    : [];
  const since = (code: string): Assignment | undefined => draft?.person.assignments.find((a) => a.code.toUpperCase() === code.toUpperCase());

  return (
    <div>
      <h2>Access and roles</h2>
      <p className="page-intro">
        Who can do what in the loans apps. A person&rsquo;s personas decide what they see and can do; a loans admin grant decides what
        they can change here. Each change is dated from the moment it is saved and recorded with your name and reason.
      </p>

      {toast && (
        <Banner tone="info">
          {toast} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={filter.trim() ? `People — ${filtered.length} of ${holders.length}` : `People — ${holders.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField label="" value={filter} onChange={setFilter} placeholder="Filter by name, email, role, persona or grant…" />
            </div>
            <Button
              variant="primary"
              onClick={() => {
                resetModal();
                setPickFilter('');
                setPicking(true);
              }}
            >
              + Add person
            </Button>
          </div>
        }
      >
        <DataTable<Person>
          columns={columns}
          rows={filtered}
          rowKey={(p) => (p.officerId !== null ? `o${p.officerId}` : `g${p.email}`)}
          emptyState={<p className="empty">{holders.length ? 'No people match the filter.' : 'Nobody holds a persona or admin grant yet.'}</p>}
        />
      </Card>

      <Card title={`Personas — ${data.personas.length}`}>
        <p className="form-field__hint">
          Read-only. Every person on the roster can open the loans apps; holding any persona also shows them every loan.
        </p>
        <DataTable<Persona> columns={personaColumns} rows={data.personas} rowKey={(p) => p.code} />
      </Card>

      {/* ── Add person: choose someone on the roster ───────────────────── */}
      <ModalShell
        open={picking}
        title="Add person"
        onClose={closeModal}
        width={620}
        actions={
          <Button onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
        }
      >
        <div className="form-stack">
          <TextField
            label="Find someone on the roster"
            value={pickFilter}
            onChange={setPickFilter}
            placeholder="Start typing a name or email…"
            hint="Only active roster people who hold no persona yet are listed. Anyone else is already on the People list — use Edit there."
          />
          {candidates.length === 0 ? (
            <p className="empty">{pickFilter.trim() ? 'Nobody on the roster matches.' : 'Everyone on the roster already holds a persona.'}</p>
          ) : (
            <DataTable<Person>
              columns={[
                { header: 'Name', cell: (p) => p.displayName, sortKey: (p) => p.displayName },
                { header: 'Email', cell: (p) => <span className="cell-desc">{p.email}</span> },
                { header: 'Job role', cell: (p) => jobRoleLabel(p.jobRole) },
                { header: '', cell: (p) => <Button onClick={() => openEdit(p)}>Choose</Button> },
              ]}
              rows={candidates}
              rowKey={(p) => `c${p.officerId}`}
            />
          )}
        </div>
      </ModalShell>

      {/* ── Edit personas ─────────────────────────────────────────────── */}
      <ModalShell
        open={draft !== null}
        title={`Personas for ${draft?.person.displayName ?? ''}`}
        onClose={closeModal}
        width={680}
        actions={
          draft && (
            <>
              <Button onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="form-field__hint">
              {draft.person.email} · {jobRoleLabel(draft.person.jobRole)}. Tick every persona this person should hold; unticking one
              ends it from the moment you save.
            </p>

            {assignable.map((p) => {
              const code = p.code.toUpperCase();
              const held = since(code);
              const notes = [
                p.description ?? '',
                held ? `Held since ${fmtCt(held.effectiveFromUtc)}, given by ${held.grantedBy}` : '',
                p.coveredByYourGrant ? '' : 'Your admin grant does not cover this persona',
              ].filter(Boolean);
              return (
                <BoolField
                  key={code}
                  label={p.name}
                  value={draft.chosen.has(code)}
                  disabled={!p.coveredByYourGrant}
                  onChange={(v) => {
                    const next = new Set(draft.chosen);
                    if (v) next.add(code);
                    else next.delete(code);
                    setDraft({ ...draft, chosen: next });
                  }}
                  hint={notes.join('. ') || undefined}
                />
              );
            })}

            {heldElsewhere.length > 0 && (
              <div className="form-field">
                <span className="form-field__label">Also held — not changed here</span>
                <ul className="link-list">
                  {heldElsewhere.map((h) => (
                    <li key={h.code} className="cell-desc">
                      <Pill tone="muted">{personaName(h.code)}</Pill> {h.why}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {draft.person.adminGrants.length > 0 && (
              <p className="form-field__hint">
                Loans admin grant: {draft.person.adminGrants.map((g) => grantLabel(g.area)).join(', ')} (on {draft.person.adminGrants[0]!.account}).
                Admin grants are not changed on this page.
              </p>
            )}
            {draft.person.assignments.some((a) => personaByCode.get(a.code.toUpperCase())?.active === false) && (
              <Banner tone="warn">This person holds a persona that is switched off. Saving removes it.</Banner>
            )}

            <ReasonField value={reason} onChange={setReason} showError={tried} asciiOnly />
          </div>
        )}
      </ModalShell>
    </div>
  );
}
